import Cookies from 'js-cookie';
import { createClient, OAuthStrategy } from '@wix/sdk';
import { members } from '@wix/members';
import { products } from '@wix/stores';
import { orders } from '@wix/pricing-plans';

const CLIENT_ID = import.meta.env.VITE_WIX_CLIENT_ID;
const SESSION_COOKIE = 'session';
const OAUTH_DATA_KEY = 'wix-oauth-redirect-data';
const MAX_RETRIES = 3;
const RETRY_DELAY = 500; // ms

class AuthService {
  isLoginProcessing = false;
  isVerifyingLogin = false;

  constructor() {
    if (AuthService.instance) {
      return AuthService.instance;
    }
    AuthService.instance = this;
    this.wixClient = createClient({
      auth: OAuthStrategy({
        clientId: CLIENT_ID,
        tokens: this.getSession(),
      }),
      modules: { members, products, orders },
    });
  }

  get client() {
    return this.wixClient;
  }

  // Session Management
  getSession() {
    return JSON.parse(Cookies.get(SESSION_COOKIE) || null);
  }

  setSession(tokens) {
    Cookies.set(SESSION_COOKIE, JSON.stringify(tokens));
    this.wixClient.auth.setTokens(tokens);
  }

  clearSession() {
    Cookies.remove(SESSION_COOKIE);
    this.clearOAuthData();
  }

  isSessionExpired() {
    const session = this.getSession();
    return (session?.accessToken?.expiresAt ?? 0) < Date.now() / 1000;
  }

  // OAuth Data Management
  getOAuthData() {
    try {
      const data = localStorage.getItem(OAUTH_DATA_KEY);
      if (!data) return null;

      const parsedData = JSON.parse(data);
      // Validate the data structure
      if (!parsedData || !parsedData.codeVerifier || !parsedData.codeChallenge || !parsedData.state) {
        console.error('Invalid OAuth data structure:', parsedData);
        this.clearOAuthData();
        return null;
      }
      return parsedData;
    } catch (error) {
      console.error('Error parsing OAuth data:', error);
      this.clearOAuthData();
      return null;
    }
  }

  setOAuthData(data) {
    if (!data || !data.codeVerifier || !data.codeChallenge || !data.state) {
      console.error('Invalid OAuth data structure:', data);
      throw new Error('Invalid OAuth data structure');
    }
    localStorage.setItem(OAUTH_DATA_KEY, JSON.stringify(data));
  }

  clearOAuthData() {
    localStorage.removeItem(OAUTH_DATA_KEY);
  }

  // Token Management
  async refreshTokens() {
    try {
      const tokens = await this.wixClient.auth.generateVisitorTokens();
      this.setSession(tokens);
      return tokens;
    } catch (error) {
      console.error('Error refreshing tokens:', error);
      this.clearSession();
      throw error;
    }
  }

  async getMemberTokens(code, state, data) {
    let tokens = null;
    let retryCount = 0;

    // Validate required OAuth parameters
    if (!code || !state || !data) {
      console.error('Missing required OAuth parameters:', { code, state, data });
      throw new Error('Missing required OAuth parameters');
    }

    // Ensure data has required fields
    if (!data.codeVerifier || !data.codeChallenge) {
      console.error('Missing required OAuth data fields:', data);
      throw new Error('Invalid OAuth data structure');
    }

    // Verify state matches
    if (state !== data.state) {
      console.error('State mismatch:', { received: state, expected: data.state });
      throw new Error('Invalid state parameter');
    }

    while (!tokens?.refreshToken?.value && retryCount < MAX_RETRIES) {
      try {
        tokens = await this.wixClient.auth.getMemberTokens(code, state, {
          redirectUri: data.redirectUri,
          codeVerifier: data.codeVerifier,
          codeChallenge: data.codeChallenge,
          state: data.state
        });

        if (tokens?.refreshToken?.value) {
          break;
        }
      } catch (error) {
        console.error(`Failed to get member tokens (attempt ${retryCount + 1}):`, {
          message: error.message,
          status: error.status,
          response: error.response?.data
        });
      }
      
      retryCount++;
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying getMemberTokens in ${RETRY_DELAY}ms...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      }
    }

    if (!tokens?.refreshToken?.value) {
      throw new Error('Failed to get refresh token after multiple attempts');
    }

    return tokens;
  }

  // Authentication Flow
  async ensureSession() {
    if (this.getOAuthData()) {
      return;
    }

    if (!this.getSession() || this.isSessionExpired()) {
      await this.refreshTokens();
    }
  }

  async login() {
    if (this.isLoginProcessing) {
      return;
    }

    this.isLoginProcessing = true;

    try {
      // Ensure CLIENT_ID is set
      if (!CLIENT_ID) {
        throw new Error('Wix Client ID is not configured. Please set VITE_WIX_CLIENT_ID environment variable.');
      }

      const redirectUri = `${window.location.origin}/login-callback`;
      const originalUri = window.location.href;

      const data = await this.wixClient.auth.generateOAuthData(
        redirectUri,
        originalUri
      );

      this.setOAuthData(data);

      const { authUrl } = await this.wixClient.auth.getAuthUrl(data);

      window.location = authUrl;
    } catch (error) {
      console.error('Error during login:', error);
      this.isLoginProcessing = false;

      // Provide more helpful error message for 400 errors
      const errorMessage = error.message || String(error);
      const errorStatus = error.status || error.response?.status;

      if (errorStatus === 400 || errorMessage.includes('400') || errorMessage.includes('Bad Request')) {
        const redirectUri = `${window.location.origin}/login-callback`;
        const helpfulMessage = 
          `Authentication failed (400 Bad Request). This usually means:\n` +
          `1. The redirect URI "${redirectUri}" is not registered in your Wix app OAuth settings\n` +
          `2. Your Wix app Client ID might be incorrect\n\n` +
          `To fix this:\n` +
          `- Go to your Wix Developer Dashboard\n` +
          `- Navigate to OAuth Settings\n` +
          `- Add "${redirectUri}" to the list of allowed redirect URIs`;
        throw new Error(helpfulMessage);
      }

      throw error;
    } finally {
      this.isLoginProcessing = false;
    }
  }

  async logout() {
    try {
      await this.wixClient.auth.logout();
      const { logoutUrl } = await this.wixClient.auth.logout(window.location.href);
      this.clearSession();
      window.location = logoutUrl;
    } catch (error) {
      console.error('Error during logout:', error);
      this.clearSession();
      throw error;
    }
  }

  async verifyLogin() {    
    // Prevent duplicate verification
    if (this.isVerifyingLogin) {
      return {
        success: false,
        error: null
      };
    }

    this.isVerifyingLogin = true;

    try {
      const data = this.getOAuthData();
      
      if (!data) {
        console.error("### verifyLogin failed: No OAuth data found");
        this.clearOAuthData(); // Clear any stale data
        return {
          success: false,
          error: 'No OAuth redirect data found. Please try logging in again.'
        };
      }

      const { code, state } = this.wixClient.auth.parseFromUrl();

      if (!code || !state) {
        console.error("### verifyLogin failed: Invalid URL parameters");
        this.clearOAuthData(); // Clear any stale data
        return {
          success: false,
          error: 'Invalid login response. Please try again.'
        };
      }

      const tokens = await this.getMemberTokens(code, state, data);
      
      if (!tokens?.refreshToken?.value) {
        console.error("### verifyLogin failed: No tokens received");
        this.clearOAuthData(); // Clear any stale data
        return {
          success: false,
          error: 'Failed to get authentication tokens. Please try again.'
        };
      }

      this.setSession(tokens);
      this.clearOAuthData();

      const redirectUri = data?.originalUri || '/';

      // Perform the redirect here in the service
      setTimeout(() => {
        window.location.replace(redirectUri);
      }, 100);

      return {
        success: true,
        redirectUri
      };
    } catch (error) {
      console.error('Login verification failed:', error);
      this.clearSession();
      this.clearOAuthData();
      return {
        success: false,
        error: error.message || 'An unexpected error occurred during login verification'
      };
    } finally {
      // Add a small delay before resetting the verification flag
      setTimeout(() => {
        this.isVerifyingLogin = false;
      }, 100);
    }
  }

  async checkAuthentication() {
    if (this.getOAuthData()) {
      return { isLoggedIn: false, email: null };
    }

    if (!this.isLoggedIn()) {
      try {
        await this.refreshTokens();
        if (!this.isLoggedIn()) {
          await this.login();
          return { isLoggedIn: false, email: null };
        }
      } catch (error) {
        console.error('Error during authentication check:', error);
        return { isLoggedIn: false, email: null };
      }
    }

    try {
      const response = await this.wixClient.members.getCurrentMember({ fieldsets: ['FULL'] });
      const email = response.member?.loginEmail ?? response.member?.profile?.email;
      return { isLoggedIn: true, email };
    } catch (error) {
      console.error('Error fetching member info:', error);
      return { isLoggedIn: false, email: null };
    }
  }

  // Utility Methods
  isLoggedIn() {
    return this.wixClient.auth.loggedIn();
  }

  async retryGetOAuthData() {
    let data = null;
    let retryCount = 0;

    while (!data && retryCount < MAX_RETRIES) {
      data = this.getOAuthData();
      if (!data) {
        retryCount++;
        if (retryCount < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        }
      }
    }

    return data;
  }
}

// Create and export a single instance
const authService = new AuthService();

// Export the instance and individual methods for convenience
export const WixClient = authService.client;
export const ensureSession = () => authService.ensureSession();
export const verifyLogin = () => authService.verifyLogin();
export const login = async () => {
  try {
    await authService.login();
  } catch (error) {
    console.error('Error during login:', error);
    // Re-throw the error so it can be handled by the UI
    throw error;
  }
}
export const logout = () => authService.logout();
export const isLoggedIn = () => authService.isLoggedIn();
export const getSession = () => authService.getSession();
export const checkAuthentication = () => authService.checkAuthentication();

// Export the service instance for advanced usage
export default authService;